import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import {
	clearPendingInviteCode,
	getPendingInviteCode,
	parseInviteCode,
	savePendingInviteCode,
} from '../../../../services/invite/pendingInviteCode';
import { TouchableOpacity } from '../../common/Touchable';

/**
 * "Have an invite code?" on the sign-in form - the fallback for when the invite link could not
 * carry the code into the app (a different device, a denied iOS paste, a store that dropped the
 * referrer). Whatever is here is the pending invite code that registration and social sign-in
 * send as `invitedBy`; it only counts for an account that sign-in creates.
 */
type Props = {
	disabled?: boolean;
	inputStyle: any;
};

export const InviteCodeField = ({ disabled, inputStyle }: Props) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState('');

	// A code captured from an invite link shows up here already filled in.
	useEffect(() => {
		getPendingInviteCode().then(code => {
			if (code) {
				setValue(code);
				setOpen(true);
			}
		});
	}, []);

	const onChange = (text: string) => {
		const next = text.trim();
		setValue(next);
		if (!next) {
			clearPendingInviteCode();
			return;
		}
		const code = parseInviteCode(`/invite/${next}`) ?? parseInviteCode(next);
		if (code) savePendingInviteCode(code);
	};

	if (!open) {
		return (
			<TouchableOpacity disabled={disabled} onPress={() => setOpen(true)} style={styles.toggle}>
				<Text style={styles.toggleText}>
					{t('T_SITE_INVITE_CODE_QUESTION', { defaultValue: 'Have an invite code?' })}
				</Text>
			</TouchableOpacity>
		);
	}

	return (
		<View style={styles.field}>
			<Text style={styles.label}>
				{t('T_SITE_INVITE_CODE_PLACEHOLDER', { defaultValue: 'Invite code (optional)' })}
			</Text>
			<TextInput
				editable={!disabled}
				value={value}
				onChangeText={onChange}
				placeholder='AB12CD'
				placeholderTextColor='#94A3B8'
				autoCapitalize='characters'
				autoCorrect={false}
				style={inputStyle}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	toggle: {
		marginTop: 6,
	},
	toggleText: {
		color: '#C6A6AE',
		fontSize: 12,
		textDecorationLine: 'underline',
	},
	field: {
		alignItems: 'flex-start',
		width: 200,
		marginTop: 6,
	},
	label: {
		color: '#B7C1D1',
		fontSize: 13,
		marginBottom: 6,
		fontWeight: '600',
		alignSelf: 'flex-start',
	},
});

export default InviteCodeField;
